import { clamp, dot, finiteVec, subtract, wrap } from './math'
import type { RacerProgress, Track, TrackSample, Vec2 } from './types'

const GATE_EPSILON = 1e-6
const GATE_HALF_WIDTH_RATIO = 0.82

function checkpointSample(track: Track, checkpointIndex: number): TrackSample | null {
  if (!Number.isInteger(checkpointIndex) || checkpointIndex < 0 || checkpointIndex >= track.checkpoints.length) {
    return null
  }
  const sampleIndex = track.checkpoints[checkpointIndex]
  if (!Number.isInteger(sampleIndex) || sampleIndex < 0 || sampleIndex >= track.samples.length) return null
  const sample = track.samples[sampleIndex]
  if (
    !sample ||
    !finiteVec(sample.position) ||
    !finiteVec(sample.tangent) ||
    !finiteVec(sample.normal) ||
    !Number.isFinite(track.width) ||
    track.width <= 0
  ) {
    return null
  }
  return sample
}

export function crossesCheckpointGate(track: Track, from: Vec2, to: Vec2, checkpointIndex: number): boolean {
  if (!finiteVec(from) || !finiteVec(to)) return false
  const checkpoint = checkpointSample(track, checkpointIndex)
  if (!checkpoint) return false

  const fromDistance = dot(subtract(from, checkpoint.position), checkpoint.tangent)
  const toDistance = dot(subtract(to, checkpoint.position), checkpoint.tangent)
  const forwardDistance = toDistance - fromDistance
  if (fromDistance > GATE_EPSILON || toDistance <= GATE_EPSILON || forwardDistance <= 0) return false

  const intersectionTime = fromDistance < 0 ? -fromDistance / forwardDistance : 0
  if (intersectionTime > 1) return false
  const intersection = {
    x: from.x + (to.x - from.x) * clamp(intersectionTime, 0, 1),
    y: from.y + (to.y - from.y) * clamp(intersectionTime, 0, 1),
  }
  const lateral = dot(subtract(intersection, checkpoint.position), checkpoint.normal)
  return Math.abs(lateral) <= track.width * GATE_HALF_WIDTH_RATIO + GATE_EPSILON
}

export function getCheckpointSegmentProgress(track: Track, progress: RacerProgress, sampleIndex: number): number {
  if (!Number.isInteger(sampleIndex) || sampleIndex < 0 || sampleIndex >= track.samples.length) return 0
  const start = checkpointSample(track, progress.lastCheckpoint)
  const end = checkpointSample(track, progress.nextCheckpoint)
  const current = track.samples[sampleIndex]
  if (!start || !end || !current || !Number.isFinite(current.distance) || !Number.isFinite(track.totalLength)) return 0

  const segmentDistance = wrap(end.distance - start.distance, track.totalLength)
  if (segmentDistance <= GATE_EPSILON) return 0
  const forwardDistance = wrap(current.distance - start.distance, track.totalLength)
  const backwardDistance = wrap(start.distance - current.distance, track.totalLength)
  if (backwardDistance < forwardDistance) return 0
  return clamp(forwardDistance / segmentDistance, 0, 1)
}
